import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider, useWatch, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ShieldCheck, ChevronLeft, ArrowRight, Loader2 } from 'lucide-react';
import type { CreativeSlotState, PublisherConfig, SelfServiceFormData, SelfServiceSubmissionRow } from '../../types/selfService';
import { equalizeSharesAcrossSlots, useCreativeSlots } from './scheduler/useCreativeSlots';
import { singleSegmentShareForWindow } from './shared/rotationSegments';
import {
  defaultFormValues,
  applyCampaignCrossFieldRules,
  businessInfoSchema,
  campaignSetupBaseSchema,
  creativeSchema,
  effectiveMinBudget,
  estimateTotalWithServiceFeeCents,
  reviewSchema,
} from '../../types/selfService';
import { isSelfServiceDevelopment } from '../../lib/env';
import SelfServiceProgressBar from './SelfServiceProgressBar';
import BusinessInfoStep from './steps/BusinessInfoStep';
import CampaignSetupStep from './steps/CampaignSetupStep';
import UploadCreativeStep from './steps/UploadCreativeStep';
import type { UncoveredRange } from './shared/creativeCoverage';
import type { OverCapRange } from './shared/creativeConcurrency';
import VerifyEmailStep from './steps/VerifyEmailStep';
import ReviewPayStep from './steps/ReviewPayStep';
import { useAuth, type AdvertiserMembership } from './auth/useAuth';
import StripePaymentSection from './StripePaymentSection';
import CampaignSummarySidebar from './CampaignSummarySidebar';
import GeoConflictModal from './shared/GeoConflictModal';
import { accentVars } from './steps/_shared';
import { apiClient, ApiError } from '../../lib/apiClient';
import {
  checkCampaignFeasibility,
  type CampaignFeasibilityResponse,
} from '../../services/feasibilityService';
import InventoryRegressionModal from './InventoryRegressionModal';
import InventoryWarningCard from './InventoryWarningCard';
import {
  useCampaignFeasibility,
  type UseCampaignFeasibilityResult,
} from '../../services/useCampaignFeasibility';
import {
  useRadiusRecommendation,
  type UseRadiusRecommendationResult,
} from '../../services/useRadiusRecommendation';
import { clearStoredIdempotencyKey, getOrCreateStoredIdempotencyKey } from '../../lib/idempotency';
import { readCapturedCode, clearCapturedCode } from './codeCapture';
import { useCodeValidation } from './useCodeValidation';
import { toLocalIsoString } from './shared/dateFormat';
import { useSelfServiceDraft } from './useSelfServiceDraft';
import { WaymarkProvider, useWaymark } from './waymark/WaymarkProvider';
import { step2GeoOutcome } from './step2GeoOutcome';
import { shouldBlockAdvanceOnFeasibility } from './feasibilityGate';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import Banner from './shared/Banner';
import { REJECTION_REASONS_BY_CODE } from './rejectionReasons';
import { validateAdSpecs, formatAdSpecViolations } from './shared/validateAdSpecs';

import { MAX_STEP, nextWizardStep, prevWizardStep, slugFromStep, type WizardStep } from './wizardSteps';

// Auto-kicks off Waymark topic search + video generation when the user
// leaves /start/business with a non-empty website URL. Lives inside the
// FormProvider + WaymarkProvider so it can read the watched URL value
// without prop-drilling. The provider itself no-ops repeat calls for the
// same URL, so this can fire generously.
const WaymarkAutoKickoff: React.FC<{ currentStep: WizardStep }> = ({ currentStep }) => {
  const { watch } = useFormContext<SelfServiceFormData>();
  const businessWebsite = watch('businessWebsite');
  const { startGenerationForUrl, isHydrating, sourceUrl, status } = useWaymark();
  React.useEffect(() => {
    if (isHydrating) return;
    if (currentStep <= 1) return;
    // Terminal failure states: don't auto-retry. Waymark returning FAILED
    // means they genuinely couldn't process the URL — re-running would just
    // create a new topic that goes through the same outcome (and meanwhile
    // 404s on status polling due to the create/poll lambda propagation race).
    // The user retries manually via the "Try again" button on the failure UI.
    if (status === 'topic_failed' || status === 'generation_failed') return;
    // Diagnostic: in uncontrolled-auth mode the user hasn't logged in to
    // Waymark yet, so background topic search / generation would error.
    // Wait for them to open the SDK UI manually instead.
    if (typeof window !== 'undefined' &&
        new URLSearchParams(window.location.search).get('waymark') === 'uncontrolled') {
      return;
    }
    const url = (businessWebsite ?? '').trim();
    if (!url) return;
    // If the persisted run was for a different URL, the provider will
    // restart. If it matches and we're already generating / ready, the
    // provider no-ops.
    void sourceUrl; void status;
    startGenerationForUrl(url);
  }, [currentStep, businessWebsite, isHydrating, sourceUrl, status, startGenerationForUrl]);
  return null;
};

// Adapter: useCampaignFeasibility needs to sit inside FormProvider (it reads
// watched values), but the wizard renders the inventory card in two different
// DOM positions (desktop sidebar + mobile above content). This bridge calls
// the hook once and lifts the result up to wizard state so both renders read
// the same verdict.
const FeasibilityBridge: React.FC<{
  config: PublisherConfig;
  onChange: (result: UseCampaignFeasibilityResult) => void;
}> = ({ config, onChange }) => {
  const result = useCampaignFeasibility(config);
  const { verdict, loading, error } = result;
  React.useEffect(() => {
    onChange({ verdict, loading, error });
  }, [verdict, loading, error, onChange]);
  return null;
};

// CTV-570 (radius-min v2): sibling bridge that owns the min-radius
// recommendation lifecycle. Same lift-to-wizard-state pattern as
// FeasibilityBridge so RadiusGeoTargeting reads a stable source.
const RadiusRecommendationBridge: React.FC<{
  config: PublisherConfig;
  onChange: (result: UseRadiusRecommendationResult) => void;
}> = ({ config, onChange }) => {
  const result = useRadiusRecommendation(config);
  const { minMiles, hasResponse, isCalculating, error, blockedReason } = result;
  React.useEffect(() => {
    onChange({ minMiles, hasResponse, isCalculating, error, blockedReason });
  }, [minMiles, hasResponse, isCalculating, error, blockedReason, onChange]);
  return null;
};

// Combined schema for the full form. CTV-533 followup: cross-field rules
// (end-date ordering, min-days, empty-custom geo) are attached here via
// superRefine so the wizard's runtime resolver enforces every synchronous
// rule. methods.trigger() and the re-validation watcher below both then
// see the same rule set without a separate manual safeParse path.
function buildFullSchema(config: PublisherConfig) {
  const minBudget = effectiveMinBudget(config.minBudget, isSelfServiceDevelopment());
  return z.object({
    ...businessInfoSchema.shape,
    ...campaignSetupBaseSchema(minBudget).shape,
    ...creativeSchema.shape,
    ...reviewSchema.shape,
  }).superRefine((data, ctx) => {
    applyCampaignCrossFieldRules(data, ctx, config.minCampaignDays);
  });
}

// Map step number to the field names it validates. CTV-533 followup:
// `targetingMode` is in step 2 so the resolver's empty-custom geo issue (which
// attaches to that path) is collected by methods.trigger(stepFields[2]).
const stepFields: Record<WizardStep, (keyof SelfServiceFormData)[]> = {
  1: ['businessName', 'businessWebsite', 'contactEmail', 'contactName', 'contactPhone'],
  2: ['budget', 'selectedProduct', 'startDate', 'endDate', 'targetMarkets', 'targetingMode'],
  3: [], // Creative-state lives outside RHF — step-3 advance gate runs inline.
  4: [], // Email verification — gated separately via verifiedEmail state
  5: ['acceptTerms', 'acceptRefundPolicy'],
};

export interface RerunFromPayload {
  // The Source campaign's submission id, used by UploadCreativeStep to fetch
  // a signed preview URL for the carried-forward creative. Optional for
  // back-compat with any in-flight handoff that lacks it (preview just stays
  // blank in that case — the rerun still works).
  sourceSubmissionId?: string;
  // CTV-466 follow-up: when true, the wizard's payment-intent submit hits
  // POST /submissions/:sourceSubmissionId/resubmit instead of POST /submissions,
  // so the rejected row is rebuilt in place rather than spawning a new one.
  // Set by FixAndResubmitButton on a creative_rejected source; RerunMenu
  // ("Run this campaign again") leaves this false so it lands as a fresh row.
  isResubmit?: boolean;
  formData: Partial<SelfServiceFormData>;
  // CTV-457: when the source campaign had multiple creatives, all of them
  // travel together. Slot 0 hydrates the legacy primary surface; slots 1..N
  // hydrate extraSlots via the scheduler hook.
  creatives?: Array<{
    storagePath: string;
    filename: string;
    mimeType: string;
    sizeBytes: number;
    clickThroughUrl?: string | null;
    startDate?: string;
    endDate?: string;
    // CTV-476: per-segment shares carried forward from the Source campaign.
    // When absent (legacy handoff or fresh single-creative rerun), the wizard
    // defaults to a single 100% element covering the window.
    segmentShares?: Array<{ startDate: string; endDate: string; share: number }>;
    // CTV-466 follow-up: when a Fix & Resubmit handoff carries a creative the
    // publisher rejected, these fields surface that on the slot card.
    sourceCreativeId?: string;
    rejected?: boolean;
    rejectionReason?: string | null;
    rejectionReasonCodes?: string[] | null;
  }>;
  // Back-compat: pre-CTV-457 handoffs carried a single-creative payload here.
  // Synthesized into `creatives[0]` when `creatives` is absent.
  creativeMeta?: {
    storagePath: string;
    filename: string;
    mimeType: string;
    sizeBytes: number;
  };
}

interface SelfServiceWizardProps {
  config: PublisherConfig;
  currentStep: WizardStep;
  onStepChange: (step: WizardStep) => void;
  onExit?: () => void;
  rerunFrom?: RerunFromPayload | null;
}

interface SelfServiceWizardBodyProps extends SelfServiceWizardProps {
  // Hoisted out of the body so the loader can show a spinner while the
  // initial draft load is in flight. Body initializes useForm from
  // `draft.loaded` directly — no post-mount `methods.reset` cascade.
  draft: ReturnType<typeof useSelfServiceDraft>;
  // CTV-471: latched in the wrapper (once per wizard entry) so it survives
  // the identity-keyed Body remounts — see the comment on SelfServiceWizard.
  skipVerify: boolean;
}

const SelfServiceWizardBody: React.FC<SelfServiceWizardBodyProps> = ({ config, currentStep, onStepChange, onExit, rerunFrom, draft, skipVerify }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, updateProfile, selectedAdvertiser, setSelectedAdvertiserId, seedMembership } = useAuth();
  // CTV-471: `skipVerify` (from the wrapper's per-wizard-entry latch) hides
  // the Verify step (4) and navigation jumps over it. For these users
  // contactEmail is always the read-only JWT email, so the auto-verify effect
  // below keeps isEmailVerified true and the downstream gate passes.
  // Tracks an in-flight ensure-self call so a re-render of the wizard doesn't
  // double-create the seat. Ref (not state) because we want strict
  // synchronous gating that doesn't depend on render order.
  const ensureSelfInFlightRef = React.useRef(false);
  const [ensureSelfError, setEnsureSelfError] = React.useState<string | null>(null);
  // CTV-562 (+ follow-up): BusinessInfoStep notifies us when the user has
  // an open seat-edit panel — either Edit details OR Add new business. The
  // step owns its own Save handler in both cases (PATCH /advertisers vs.
  // POST /memberships/ensure-self); the wizard just gates Continue on this
  // signal so step 1's two persistence locations (draft + seat) can't
  // drift mid-edit.
  const [step1Editing, setStep1Editing] = React.useState(false);
  // True when step 2's custom-geo picker has data on >1 tab and the user
  // hasn't picked one yet for this advance. Cleared the moment they confirm.
  const [geoConflictOpen, setGeoConflictOpen] = React.useState(false);
  const [wizardAdvertiserId, setWizardAdvertiserId] = React.useState<string | null>(null);
  // Resolved advertiser id used by CampaignSetupStep's collision check and by
  // validateCurrentStep to gate advance on step 2. Returning members carry an
  // advertiser from useAuth; brand-new buyers don't have one until step 4's
  // ensure-self call lands, in which case the check is skipped.
  const resolvedAdvertiserId = selectedAdvertiser?.advertiserId ?? wizardAdvertiserId;
  const [verifiedEmail, setVerifiedEmail] = React.useState<string | null>(null);
  const [completedSteps, setCompletedSteps] = React.useState<number[]>([]);
  const [visitedSteps, setVisitedSteps] = React.useState<number[]>([currentStep]);
  // Persisted to localStorage so a page reload mid-flow can pass it back as
  // `previousSubmissionId` on the next POST /submissions, letting the API
  // clean up the orphan pending_payment row instead of leaking a duplicate.
  // CTV-554: scoped per (slug, advertiser, user) — same shape as the draft
  // slot — so a seat switch or sign-out doesn't bleed submissionId or
  // idempotency-key across identities. `resolvedAdvertiserId` (not just
  // selectedAdvertiser?.advertiserId) so brand-new buyers' keys flip from
  // 'anon' to their freshly-minted advertiser after step 4's ensure-self.
  const submissionSlotSuffix = `${config.slug}_${resolvedAdvertiserId ?? 'anon'}_${user?.id ?? 'anon'}`;
  const submissionIdKey = `selfservice_submission_id_${submissionSlotSuffix}`;
  const paymentIdempotencyKeyKey = `selfservice_payment_idempotency_key_${submissionSlotSuffix}`;
  const [submissionId, setSubmissionIdState] = React.useState<string | null>(() => {
    try { return localStorage.getItem(submissionIdKey); } catch { return null; }
  });
  // CTV-587/588: in the deferred flow the intent (and thus the submission id)
  // is created inside the Pay handler, then the SAME handler confirms and calls
  // handlePaymentSuccess. A state-closure read of `submissionId` there would be
  // stale (still the pre-Pay value), so mirror it into a ref that updates
  // synchronously and read the ref where the just-created id is needed.
  const submissionIdRef = React.useRef(submissionId);
  const setSubmissionId = React.useCallback((id: string | null) => {
    submissionIdRef.current = id;
    setSubmissionIdState(id);
    try {
      if (id) localStorage.setItem(submissionIdKey, id);
      else localStorage.removeItem(submissionIdKey);
    } catch { /* private mode — non-fatal */ }
  }, [submissionIdKey]);
  const clearPaymentIdempotencyKey = React.useCallback(() => {
    clearStoredIdempotencyKey(paymentIdempotencyKeyKey);
  }, [paymentIdempotencyKeyKey]);
  // CTV-587/588: with the deferred-intent flow the Stripe intent isn't created
  // until Pay, so there's no client secret to hold in state and no per-plan
  // clientSecret churn to remount the Payment Element. The server-authoritative
  // amount + fee are still captured on creation for the receipt fallback.
  const [paymentAmountCents, setPaymentAmountCents] = React.useState<number | null>(null);
  const [paymentFeeCents, setPaymentFeeCents] = React.useState<number | null>(null);
  // CTV-587/588: Stripe CustomerSession secret for the pay-step Payment Element
  // (enables save-card + redisplay of saved cards). Fetched once on reaching
  // step 5 while signed in; plan-independent, so a plan toggle never refetches.
  const [customerSessionSecret, setCustomerSessionSecret] = React.useState<string | null>(null);
  const customerSessionInFlightRef = React.useRef(false);
  const [paymentError, setPaymentError] = React.useState<string | null>(null);
  // Set when the submit-time inventory re-check disagrees with the live verdict
  // the buyer saw at step 2. Opens the regression modal; null = no regression.
  const [regressionVerdict, setRegressionVerdict] =
    React.useState<CampaignFeasibilityResponse | null>(null);
  // Live inventory verdict lifted out of FeasibilityBridge so the card can
  // render in both the desktop sidebar column and the mobile above-content
  // slot from a single hook call.
  const [feasibility, setFeasibility] = React.useState<UseCampaignFeasibilityResult>({
    verdict: null,
    loading: false,
    error: null,
  });
  // CTV-570 (radius-min v2): sibling state lifted out of
  // RadiusRecommendationBridge. Read by CampaignSetupStep →
  // GeoTargetingTabs → RadiusGeoTargeting for the slider floor.
  const [radiusRecommendation, setRadiusRecommendation] =
    React.useState<UseRadiusRecommendationResult>({
      minMiles: null,
      hasResponse: false,
      isCalculating: false,
      error: null,
      blockedReason: null,
    });
  // Dev-only bypass toggled from InventoryDiagnostic. When true, the step-2
  // Continue gate, helper text, and submit-time re-check all skip the
  // infeasible verdict so testers can submit while the booked-aggregate math
  // is being fixed (CTV-536). The toggle itself is only renderable inside the
  // dev-only diagnostic card, so prod builds can never reach this state.
  const [bypassFeasibility, setBypassFeasibility] = React.useState(false);
  // True between Stripe Elements confirming and the wizard navigating to
  // SubmittedPage. While true the payment section is swapped for an
  // inline "Finalizing your order…" indicator so the buyer sees feedback
  // while /submissions/:id/finalize is in flight.
  const [finalizing, setFinalizing] = React.useState(false);
  // Synchronous guard against a double Pay click racing two intent creates —
  // each would otherwise insert its own submission row. The Payment Element's
  // own busy state drives the visible spinner; this ref just enforces the lock.
  const creatingIntentLockRef = React.useRef(false);
  type CreativeMeta = {
    storagePath: string;
    filename: string;
    mimeType: string;
    sizeBytes: number;
  };
  // CTV-466 follow-up: when set, the wizard's submit will POST
  // /submissions/:resubmitTargetSubmissionId/resubmit instead of /submissions,
  // so the rejected row is rebuilt in place. Stays sticky across creative
  // swaps because the target row to update is fixed for the wizard session.
  const [resubmitTargetSubmissionId, setResubmitTargetSubmissionId] = React.useState<string | null>(null);

  const fullSchema = React.useMemo(() => buildFullSchema(config), [config]);

  // The body is only mounted by the loader once `draft.isLoading` is false,
  // so `draft.loaded` is settled here. Build a single seed that mirrors the
  // old hydration logic (persisted formData ⊕ user/seat identity ⊕ rerun
  // handoff ⊕ campaign-step auto-defaults) and feed it to useForm's
  // defaultValues directly. This replaces the previous post-mount
  // `methods.reset(seed)` cascade that was dropping focus on the campaign
  // and budget inputs because it fired AFTER first paint, broadcasting a
  // change for every registered field while the user was already typing.
  //
  // Evaluated exactly once on first render — useForm only consumes
  // defaultValues at mount, and the loader guarantees the draft has loaded.
  const seed = React.useMemo<SelfServiceFormData>(() => {
    // Rerun handoff wins: source-campaign data overrides any persisted
    // draft so the buyer reviews-and-tweaks the chosen source, not a stale
    // half-finished draft from another visit.
    if (rerunFrom) {
      return {
        ...defaultFormValues,
        ...(rerunFrom.formData as SelfServiceFormData),
        // CTV-592: contact fields follow the current submitter, never the
        // source campaign, see CONTEXT.md. Overlay AFTER the rerun payload so
        // a stale caller still shipping contact keys can't clobber the user
        // profile (defense-in-depth: RerunMenu / FixAndResubmitButton no
        // longer include them).
        contactEmail: user?.email ?? '',
        contactName: user?.contactName ?? '',
        contactPhone: user?.contactPhone ?? '',
        acceptTerms: false,
        acceptRefundPolicy: false,
        paymentPlan: 'upfront',
      };
    }

    // CTV-502: legacy drafts saved before the ZIPs tab was removed may
    // still carry geoMode='zips' (and the now-deleted selectedZIPs /
    // zipsInputBuffer fields). Coerce to radius and drop the stale fields
    // so the wizard mounts on a supported tab and the form schema stays clean.
    const persisted = { ...(draft.loaded?.formData ?? {}) } as Record<string, unknown>;
    if (persisted.geoMode === 'zips') persisted.geoMode = 'radius';
    delete persisted.selectedZIPs;
    delete persisted.zipsInputBuffer;
    // Drop empty/missing values for contact + seat fields so the user/seat
    // fallbacks below win. A prior session that saved the draft before
    // `/profiles/me` resolved persisted these as `""` — without this strip
    // they'd clobber the populated profile values.
    //
    // businessName / businessWebsite are SEAT-OWNED — the membership is the
    // source of truth for the currently-selected advertiser, and CTV-562's
    // step-1 inline edit PATCHes the seat directly. So when a seat is present,
    // strip those two from `persisted` unconditionally — a multi-seat user
    // switching to a different advertiser must see the new seat's values on
    // remount, not whatever the prior session typed into the form for that
    // advertiser. The anonymous path keeps the empty-only strip so a typed
    // business survives a refresh before sign-in.
    for (const k of [
      'contactName',
      'contactPhone',
      'contactEmail',
    ] as const) {
      if (!persisted[k]) delete persisted[k];
    }
    if (selectedAdvertiser) {
      delete persisted.businessName;
      delete persisted.businessWebsite;
    } else {
      if (!persisted.businessName) delete persisted.businessName;
      if (!persisted.businessWebsite) delete persisted.businessWebsite;
    }

    // Campaign-step auto-defaults — previously lived as post-mount
    // `setValue` effects in CampaignSetupStep, which fired their own
    // broadcasts after the inputs had already been mounted. Folding them
    // into the seed means they're present from first render.
    const autoDefaults: Partial<SelfServiceFormData> = {};
    if (config.products[0]?.id) autoDefaults.selectedProduct = config.products[0].id;
    if (config.markets.length === 1) autoDefaults.targetMarkets = [config.markets[0].dmaCode];

    return {
      ...defaultFormValues,
      ...autoDefaults,
      businessName: selectedAdvertiser?.advertiserName ?? '',
      businessWebsite: selectedAdvertiser?.businessWebsite ?? '',
      contactEmail: user?.email ?? '',
      contactName: user?.contactName ?? '',
      contactPhone: user?.contactPhone ?? '',
      ...(persisted as Partial<SelfServiceFormData>),
      // Terms / refund must be re-checked on every visit regardless of persisted state.
      acceptTerms: false,
      acceptRefundPolicy: false,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const methods = useForm<SelfServiceFormData>({
    defaultValues: seed,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- zodResolver vs. useForm Resolver type mismatch
    resolver: zodResolver(fullSchema) as any,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  // CTV-533 followup: drive per-step re-validation explicitly so an error
  // chip clears the moment the buyer fixes the underlying state. RHF's
  // built-in `reValidateMode: 'onChange'` only engages once
  // `formState.isSubmitted` is true, and `methods.trigger()` (what
  // validateCurrentStep calls from the Continue button) doesn't set that
  // flag. We subscribe to RHF's change stream via `methods.watch(callback)`
  // — the subscription fires only on actual value writes, NOT on every
  // render (which is what `useWatch` does, and what created an infinite
  // re-render / re-trigger loop here in an earlier draft). When a current-
  // step field changes AND any step field currently carries an error,
  // re-trigger validation for the whole step. No errors → no triggers,
  // preserving the "errors only on Continue" contract.
  React.useEffect(() => {
    const fields = stepFields[currentStep] as (keyof SelfServiceFormData)[];
    const subscription = methods.watch((_values, info) => {
      if (info.type !== 'change' || !info.name) return;
      if (!fields.includes(info.name as keyof SelfServiceFormData)) return;
      const hasAnyError = fields.some((f) => methods.formState.errors[f]);
      if (hasAnyError) {
        void methods.trigger(fields);
      }
    });
    return () => subscription.unsubscribe();
  }, [currentStep, methods]);

  // "Run this Campaign Again" handoff — populate the form from the Source
  // campaign and land the user on the Campaign step for a single review-tweak
  // pass. Fires exactly once, before draft hydration so it wins over any
  // lingering draft. Budget + dates + campaign name are now prefilled by the
  // helpers in RerunMenu; payment plan is forced to upfront here regardless
  // of the Source's plan (per CTV-449).
  const rerunAppliedRef = React.useRef(false);
  React.useEffect(() => {
    if (!rerunFrom || rerunAppliedRef.current) return;
    rerunAppliedRef.current = true;
    // The form values were already baked into the seed via the `rerunFrom`
    // branch above — no methods.reset() needed here. We only handle the
    // side-effects: blow away any lingering draft state, reset the slot
    // hook, mark steps visited, etc.
    draft.clear();
    setSubmissionId(null);
    clearPaymentIdempotencyKey();
    // Source campaigns with multiple creatives carry all of them. Slot 0 +
    // slots 1..N hydrate the unified slots hook in one call. Pre-CTV-457
    // handoffs (creativeMeta only) collapse to a 1-element array here.
    const sourceCreatives = rerunFrom.creatives && rerunFrom.creatives.length > 0
      ? rerunFrom.creatives
      : rerunFrom.creativeMeta
        ? [{
            storagePath: rerunFrom.creativeMeta.storagePath,
            filename: rerunFrom.creativeMeta.filename,
            mimeType: rerunFrom.creativeMeta.mimeType,
            sizeBytes: rerunFrom.creativeMeta.sizeBytes,
          }]
        : [];
    const formStart = (rerunFrom.formData.startDate as string | undefined) ?? '';
    const formEnd = (rerunFrom.formData.endDate as string | undefined) ?? '';
    const prefilledSlots: CreativeSlotState[] = sourceCreatives.map((c, idx) => {
      const startDate = c.startDate ?? formStart;
      const endDate = c.endDate ?? formEnd;
      return {
        slotId: typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `rerun-${idx}-${Date.now()}`,
        storagePath: c.storagePath,
        filename: c.filename,
        mimeType: c.mimeType,
        sizeBytes: c.sizeBytes,
        clickThroughUrl: c.clickThroughUrl ?? '',
        segmentShares: c.segmentShares && c.segmentShares.length > 0
          ? c.segmentShares.map((s) => ({ ...s }))
          : singleSegmentShareForWindow(startDate, endDate),
        startDate,
        endDate,
        rejected: c.rejected,
        rejectionReason: c.rejectionReason ?? null,
        rejectionReasonCodes: c.rejectionReasonCodes ?? null,
        sourceCreativeId: c.sourceCreativeId,
      };
    });
    // "Run this Campaign Again" strips dates + shares from the payload (see
    // RerunMenu in campaignActions.tsx), expecting the wizard to default each
    // slot to `singleSegmentShareForWindow(...)` (share=100). For a single
    // creative that's fine; for N≥2 on identical windows it would sum to
    // 100·N and `assertSegmentSharesValid` would reject the submission at the
    // payment step. Detect the "no carried shares" case and even-split before
    // reset — Fix & Resubmit (same effect, different payload) does carry real
    // shares and must NOT be re-equalized (the buyer may have hand-tweaked).
    const carriedAnyShares = sourceCreatives.some(
      (c) => Array.isArray(c.segmentShares) && c.segmentShares.length > 0,
    );
    slots.reset(carriedAnyShares ? prefilledSlots : equalizeSharesAcrossSlots(prefilledSlots));
    setResubmitTargetSubmissionId(
      rerunFrom.isResubmit && rerunFrom.sourceSubmissionId ? rerunFrom.sourceSubmissionId : null,
    );
    // Mark steps 1 + 3 + 4 as visited so the tracker reflects what's already
    // pre-filled. Step 2 (Campaign) stays the current focus. Step 1
    // (Business) is also "completed" — its data carried over from the source
    // submission — so seed completedSteps too. Without that, the
    // before-current pill (step 1) would still render correctly under the
    // positional rule, but other consumers of `completedSteps` (analytics,
    // future gating) would see a hole where step 1 should be.
    setVisitedSteps([1, 2, 3, 4]);
    setCompletedSteps([1]);
    // `slots` is the return value of useCreativeSlots — a fresh object
    // every render. Including it would re-fire the rerun handoff on every
    // render and re-clobber form state; rerunAppliedRef gates the actual
    // body to once-per-rerun semantics.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerunFrom, draft, setSubmissionId, clearPaymentIdempotencyKey]);

  // Draft hydration + save effects live further down the body, after the
  // multi-commercial state (extra slots, primary slot schedule) is
  // declared — see the "CTV-457 draft persistence" block below. The
  // `draftSave` / `draftIsLoading` aliases stay declared here so the
  // codes-hydration effect below can keep using them.
  //
  // We intentionally do NOT pull form values via `useWatch({ control })` —
  // a no-name useWatch subscribes to every field and re-renders the wizard
  // on every keystroke. With the persist effect, the Stripe-intent
  // invalidation effect, and the FeasibilityBridge cycle all reading
  // watched values, the wizard-wide rerender churn occasionally clipped
  // the campaign-name input's RHF ref callback mid-keystroke, dropping
  // focus. The persist effect below uses `methods.watch(callback)` instead
  // (same subscription pattern as the existing CTV-533 step-validation
  // watcher), so saves happen on actual writes, not on render.
  const draftSave = draft.save;
  const draftIsLoading = draft.isLoading;

  // Pull captured ?ref= / ?promo= codes out of localStorage into the form
  // when the form's fields are still empty. Runs once draft hydration has
  // settled so it doesn't fight the draft. If the draft already has codes,
  // those win; otherwise we adopt whatever was captured most recently.
  React.useEffect(() => {
    if (draftIsLoading || !config.slug) return;
    const cur = methods.getValues();
    if (!cur.referralCode) {
      const ref = readCapturedCode('ref', config.slug);
      if (ref) methods.setValue('referralCode', ref);
    }
    if (!cur.promoCode) {
      const promo = readCapturedCode('promo', config.slug);
      if (promo) methods.setValue('promoCode', promo);
    }
  }, [draftIsLoading, config.slug, methods]);

  // Hydrate fields when user signs in mid-wizard OR when the selected
  // advertiser seat changes (multi-membership users switching seats via the
  // HeaderAuth dropdown). Business fields come from the seat; contact fields
  // from the user's profile.
  React.useEffect(() => {
    if (!user) return;
    const current = methods.getValues();
    const patch: Partial<SelfServiceFormData> = {};
    if (!current.businessName && selectedAdvertiser?.advertiserName) {
      patch.businessName = selectedAdvertiser.advertiserName;
    }
    if (!current.businessWebsite && selectedAdvertiser?.businessWebsite) {
      patch.businessWebsite = selectedAdvertiser.businessWebsite;
    }
    if (!current.contactEmail && user.email) patch.contactEmail = user.email;
    if (!current.contactName && user.contactName) patch.contactName = user.contactName;
    if (!current.contactPhone && user.contactPhone) patch.contactPhone = user.contactPhone;
    if (Object.keys(patch).length > 0) {
      Object.entries(patch).forEach(([k, v]) => methods.setValue(k as keyof SelfServiceFormData, v as never));
    }
  }, [user, selectedAdvertiser, methods]);

  // Multi-seat switch: when the selected advertiser actually CHANGES (vs the
  // first-fill above which only patches empty fields), force-replace the
  // business identity in the form. The in-wizard dropdown only renders in
  // BusinessInfoStep's read-only summary, so any staged edits to those two
  // fields can't be live when this fires. Contact fields (email/name/phone)
  // describe the submitter, not the seat — they stay put.
  const lastAppliedAdvertiserIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const id = selectedAdvertiser?.advertiserId ?? null;
    const prev = lastAppliedAdvertiserIdRef.current;
    if (id && prev && id !== prev) {
      methods.setValue('businessName', selectedAdvertiser!.advertiserName, { shouldDirty: true });
      methods.setValue('businessWebsite', selectedAdvertiser!.businessWebsite ?? '', { shouldDirty: true });
    }
    lastAppliedAdvertiserIdRef.current = id;
  }, [selectedAdvertiser, methods]);

  // Scroll to top whenever the wizard step changes.
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [currentStep]);

  const uploadCreativeFile = React.useCallback(async (file: File): Promise<CreativeMeta> => {
    analytics.track(EVENTS.CREATIVE_UPLOAD_STARTED, { station_slug: config.slug });
    try {
      const { uploadUrl, storagePath } = await apiClient.post<{
        uploadUrl: string;
        storagePath: string;
      }>('/creatives/upload-url', {
        slug: config.slug,
        filename: file.name,
        mimeType: file.type || 'video/mp4',
        sizeBytes: file.size,
      });
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': file.type || 'video/mp4' },
        body: file,
      });
      if (!putRes.ok) throw new Error(`Video upload failed (${putRes.status})`);
      // CTV-508: kick off server-side poster (non-black thumbnail) generation
      // now that the file has landed. Best-effort and awaited so the poster is
      // usually ready by the time the creative renders; a failure is swallowed
      // (renderers fall back to the `#t=0.5` <video> seek). This same call
      // covers Waymark commits, which funnel through this exact function.
      try {
        await apiClient.post('/creatives/poster', { slug: config.slug, storagePath });
      } catch {
        /* poster is decorative — never fail the upload over it */
      }
      const meta: CreativeMeta = {
        storagePath,
        filename: file.name,
        mimeType: file.type || 'video/mp4',
        sizeBytes: file.size,
      };
      analytics.track(EVENTS.CREATIVE_UPLOAD_SUCCEEDED, {
        station_slug: config.slug,
        size_bytes: file.size,
      });
      return meta;
    } catch (e) {
      // Sanitized: HTTP status only, never the raw message.
      analytics.track(EVENTS.CREATIVE_UPLOAD_FAILED, {
        station_slug: config.slug,
        status: (e as { status?: number })?.status,
      });
      throw e;
    }
  }, [config.slug]);

  // Unified state for every Creative slot (slot 0 lives at slots[0]). The
  // scheduler reads the same array — there is no parallel "primary" tracking.
  const watchedStartDate0 = useWatch({ control: methods.control, name: 'startDate' }) || '';
  const watchedEndDate0 = useWatch({ control: methods.control, name: 'endDate' }) || '';
  const slots = useCreativeSlots({
    campaignStartDate: watchedStartDate0,
    campaignEndDate: watchedEndDate0,
    uploadFile: uploadCreativeFile,
  });

  // CTV-466 follow-up: when the wizard was opened from a Fix & Resubmit
  // handoff, voided creatives are mirrored onto the slot state with
  // `rejected: true`. The Continue button on step 3 stays disabled until
  // the advertiser either uploads a replacement or removes each rejected
  // slot — replacing via file/library/Waymark clears the flag in the slot
  // state, and Remove drops the slot entirely.
  const hasUnresolvedRejections = React.useMemo(
    () => slots.slots.some((s) => s.rejected),
    [slots.slots],
  );

  // ADR-0006 per-day creative coverage. UploadCreativeStep bubbles the gap
  // ranges up so we can both gate Continue and surface the same list under
  // the scheduler. Starts as a non-empty gap so step 3 enters disabled until
  // the first commercial is added — matches the previous "needs a creative
  // file" implicit gate.
  const [creativeCoverageGaps, setCreativeCoverageGaps] = React.useState<UncoveredRange[]>([
    { startDate: '0000-00-00', endDate: '0000-00-00' },
  ]);
  const hasCreativeCoverageGap = creativeCoverageGaps.length > 0;
  // ADR-0008: per-day concurrency cap. Unlike coverage, the default state is
  // "within cap" because zero commercials trivially satisfies the 8-creative
  // ceiling — the coverage gate already blocks Continue when slots are empty.
  const [creativeOverCapRanges, setCreativeOverCapRanges] = React.useState<OverCapRange[]>([]);
  const hasCreativeOverCap = creativeOverCapRanges.length > 0;

  // -----------------------------------------------------------------
  // Draft persistence — restore + save the full commercial state.
  // -----------------------------------------------------------------

  // Restore persisted creative slots + visited steps on first mount. Form
  // values come from `useForm`'s `defaultValues` (the seed builder above);
  // this effect only handles the bits that live outside RHF state. Skipped
  // on a rerun handoff — that path resets slots / visited via its own effect.
  const slotsHydratedRef = React.useRef(false);
  React.useEffect(() => {
    if (slotsHydratedRef.current) return;
    slotsHydratedRef.current = true;
    if (rerunFrom) return;
    if (!draft.loaded) return;
    const savedStep = Math.max(1, Math.min(MAX_STEP, draft.loaded.currentStep)) as WizardStep;
    setVisitedSteps(Array.from({ length: savedStep }, (_, i) => i + 1));
    // The buyer continued past every step before `savedStep` in the original
    // session, so mark them completed. The current step itself isn't
    // completed (they're about to redo it). Without this, the pill rendering
    // (which is positional) still looks right, but `completedSteps` would
    // disagree with the visual on resume — an easy footgun for callers that
    // read it.
    if (savedStep > 1) {
      setCompletedSteps(Array.from({ length: savedStep - 1 }, (_, i) => i + 1));
    }
    const saved = draft.loaded.creatives;
    if (Array.isArray(saved) && saved.length > 0) {
      const valid: CreativeSlotState[] = saved
        .filter((s): s is CreativeSlotState =>
          !!s && typeof s.slotId === 'string' && typeof s.filename === 'string',
        )
        .map((s) => {
          const startDate = s.startDate ?? '';
          const endDate = s.endDate ?? '';
          return {
            slotId: s.slotId,
            storagePath: s.storagePath ?? null,
            filename: s.filename ?? '',
            mimeType: s.mimeType ?? 'video/mp4',
            sizeBytes: typeof s.sizeBytes === 'number' ? s.sizeBytes : 0,
            clickThroughUrl: s.clickThroughUrl ?? '',
            segmentShares: Array.isArray(s.segmentShares) && s.segmentShares.length > 0
              ? s.segmentShares.map((x) => ({ ...x }))
              : singleSegmentShareForWindow(startDate, endDate),
            startDate,
            endDate,
            // Fix & Resubmit handoff metadata — without these the rejection
            // banner disappears on reload and the Next-button gate at
            // `slotsAllValid` lets the user move past an un-replaced creative.
            rejected: s.rejected,
            rejectionReason: s.rejectionReason ?? null,
            rejectionReasonCodes: s.rejectionReasonCodes ?? null,
            sourceCreativeId: s.sourceCreativeId,
          };
        });
      if (valid.length > 0) slots.reset(valid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist on every form-value or step change (debounced inside the hook).
  // Subscribes via `methods.watch(callback)` so the wizard does NOT re-render
  // on every keystroke (see the comment on the broad-useWatch removal above).
  React.useEffect(() => {
    if (draftIsLoading) return;
    const saveCurrent = () => {
      // Never persist the terms / refund acknowledgement — user must re-check
      // them every time they re-enter the wizard.
      const values = methods.getValues() as unknown as Record<string, unknown>;
      const { acceptTerms: _at, acceptRefundPolicy: _ar, ...persistedFormData } = values;
      void _at; void _ar;
      draftSave({
        currentStep,
        formData: persistedFormData,
        creatives: slots.slots,
      });
    };
    // Initial save covers step / slot changes (which aren't form fields and so
    // aren't seen by the subscription below).
    saveCurrent();
    const subscription = methods.watch((_values, info) => {
      if (info.type !== 'change') return;
      saveCurrent();
    });
    return () => subscription.unsubscribe();
  }, [
    currentStep,
    slots.slots,
    draftSave,
    draftIsLoading,
    methods,
  ]);

  // Waymark "Run this creative" bookkeeping. waymarkSlotKey tracks which
  // slot id currently holds the Waymark video. Cleared when the user clears
  // or removes that slot, which lets the panel restore its preview-with-
  // buttons state. Local session state — the draft persists the slot's
  // storagePath but not this provenance flag.
  const [waymarkSlotKey, setWaymarkSlotKey] = React.useState<string | null>(null);

  const handleAddSlot = React.useCallback(() => {
    // Don't touch any existing slot's weight or schedule when the user adds
    // a slot — the new slot is empty and therefore invisible to the
    // scheduler, so prematurely rebalancing would leave the visible bar
    // summing to <100% (gap on the right). The `visibleIdsString` effect
    // below fires an even rebalance the moment the new slot gets content.
    slots.addSlot();
  }, [slots]);

  // Compute the list of slots that are currently "visible" (have content) in
  // the same order the scheduler renders them.
  // CTV-476: per-segment shares are owned by the Scheduler component (which
  // owns the per-segment recency stack) and by `useCreativeSlots` (which
  // re-derives on add/remove/date-change). The wizard only forwards
  // segmentShares patches via the existing onSlotChange path.
  const handleResetWeights = React.useCallback(() => {
    slots.resetWeights();
  }, [slots]);

  // Wrap slots.handleFilePick and slots.removeSlot so a clear/remove of the
  // Waymark-bearing slot also resets the provenance flag. Wizard owns the
  // bookkeeping because the panel needs to know to re-show its buttons.
  const handleSlotFileChange = React.useCallback((slotId: string, file: File | null) => {
    if (!file && waymarkSlotKey === slotId) {
      setWaymarkSlotKey(null);
    }
    slots.handleFilePick(slotId, file);
  }, [slots, waymarkSlotKey]);

  const handleSlotRemove = React.useCallback((slotId: string) => {
    if (waymarkSlotKey === slotId) {
      setWaymarkSlotKey(null);
    }
    slots.removeSlot(slotId);
  }, [slots, waymarkSlotKey]);

  const handleSlotPickFromLibrary = React.useCallback(
    (slotId: string, pick: { storagePath: string; filename: string; mimeType: string; sizeBytes: number; clickThroughUrl: string }) => {
      if (waymarkSlotKey === slotId) {
        // The library pick replaces a Waymark commit on this slot.
        setWaymarkSlotKey(null);
      }
      slots.applyLibraryPick(slotId, pick);
    },
    [slots, waymarkSlotKey],
  );

  // CTV-563: Waymark renders occasionally come back out-of-spec (the SDK's
  // known "sometimes returns a 15s render against duration: 30" bug). When
  // that happens we surface a rejection banner in step 3 and hold the slot
  // empty until the buyer either retries generation or uploads their own
  // commercial.
  const [waymarkRejection, setWaymarkRejection] = React.useState<string | null>(null);
  const clearWaymarkRejection = React.useCallback(() => setWaymarkRejection(null), []);

  // Commit handler — invoked from the WaymarkPanel's "Run this creative"
  // button (in UploadCreativeStep). Slot 0 empty → land in slot 0; else
  // append a new slot. The fetched blob is wrapped in a File so the same
  // upload pipeline picks it up unchanged.
  const handleCommitWaymark = React.useCallback(async (blob: Blob, videoId: string) => {
    const specs = await validateAdSpecs(blob);
    if (!specs.ok) {
      analytics.track(EVENTS.WAYMARK_REJECTED_SPECS, {
        station_slug: config.slug,
        violations: specs.violations.map((v) => v.kind),
      });
      setWaymarkRejection(formatAdSpecViolations(specs.violations));
      return;
    }
    setWaymarkRejection(null);
    analytics.track(EVENTS.WAYMARK_COMMITTED, { station_slug: config.slug });
    const file = new File([blob], `waymark-${videoId}.mp4`, { type: blob.type || 'video/mp4' });
    const slot0 = slots.slots[0];
    const slot0Empty = !slot0 || (!slot0.storagePath && !slot0.filename);
    if (slot0Empty && slot0) {
      slots.handleFilePick(slot0.slotId, file);
      setWaymarkSlotKey(slot0.slotId);
    } else {
      const slotId = slots.addSlot();
      slots.handleFilePick(slotId, file);
      setWaymarkSlotKey(slotId);
    }
  }, [slots, config.slug]);

  // Replace handler — invoked when the user clicks Customize on the Waymark
  // slot, edits in the SDK, and a fresh renderedUrl lands. We replace the
  // existing slot's content in place (same slotId, fresh upload).
  const handleReplaceWaymark = React.useCallback(async (blob: Blob, videoId: string) => {
    if (waymarkSlotKey === null) return;
    const specs = await validateAdSpecs(blob);
    if (!specs.ok) {
      analytics.track(EVENTS.WAYMARK_REJECTED_SPECS, {
        station_slug: config.slug,
        violations: specs.violations.map((v) => v.kind),
      });
      setWaymarkRejection(formatAdSpecViolations(specs.violations));
      return;
    }
    setWaymarkRejection(null);
    const file = new File([blob], `waymark-${videoId}.mp4`, { type: blob.type || 'video/mp4' });
    slots.handleFilePick(waymarkSlotKey, file);
  }, [waymarkSlotKey, slots, config.slug]);

  const validateCurrentStep = async (): Promise<boolean> => {
    const fields = stepFields[currentStep];

    // CTV-533 followup: buildFullSchema now applies the campaign-step
    // cross-field rules via superRefine, so methods.trigger(fields) catches
    // every synchronous rule in one call. Errors surface through RHF's
    // resolver path — the per-step useEffect watcher below re-runs trigger
    // on each value change so chips clear the moment the buyer fixes them.
    const baseValid = await methods.trigger(fields as (keyof SelfServiceFormData)[]);

    // Step 2 has an additional async check (server collision) that can't
    // live in the resolver — it round-trips to /submissions/check-name. Run
    // it only after the synchronous rules pass so the buyer doesn't get
    // hit with a collision chip while other fields are still empty.
    if (currentStep === 2 && baseValid) {
      const values = methods.getValues();
      if (resolvedAdvertiserId && values.startDate) {
        try {
          const { available } = await apiClient.post<{ available: boolean }>(
            '/submissions/check-name',
            {
              slug: config.slug,
              advertiserId: resolvedAdvertiserId,
              startDate: values.startDate,
              campaignName: values.campaignName?.trim() || undefined,
              submissionId: submissionId ?? undefined,
            },
          );
          if (!available) {
            analytics.track(EVENTS.CAMPAIGN_NAME_CONFLICT, { station_slug: config.slug });
            methods.setError('campaignName', {
              type: 'collision',
              message:
                'You already have an active campaign at this station with this name — give this one a unique name to continue.',
            });
            // The name field sits at the very top of step 2; without scroll-to,
            // a buyer who hit Continue from below the fold sees nothing change.
            const el = document.getElementById('campaignName') as HTMLInputElement | null;
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el?.focus({ preventScroll: true });
            return false;
          }
        } catch {
          // Network/server failure shouldn't block advance — the create() call
          // backstops with the same check and surfaces a 400 if it really collides.
        }
      }
    }

    return baseValid;
  };

  const markStepCompleted = (step: WizardStep) => {
    setCompletedSteps((prev) => (prev.includes(step) ? prev : [...prev, step]));
  };

  const goToStep = (step: WizardStep) => {
    onStepChange(step);
    setVisitedSteps((prev) => (prev.includes(step) ? prev : [...prev, step]));
  };

  /**
   * CTV-474: gate forward navigation into the creative / review / pay steps
   * on the inventory verdict. Covers three bypass paths:
   *   - Continue button from step 2
   *   - Step-rail click from step 2 to step >= 3
   *   - Step-rail click from step 1 to step >= 3 (skipping step 2)
   *
   * Gated states:
   *   1. Loading: the buyer just changed inputs and the request is in flight
   *      (or the spinner is about to start). Advancing past step 2 here would
   *      leave them on step 3 before the new verdict lands.
   *   2. Explicit infeasible verdict.
   *
   * A null + non-loading verdict is treated as "no verdict, no gate," which
   * keeps the wizard usable when the buyer simply hasn't reached step 2 yet.
   */
  const isFeasibilityInfeasible =
    feasibility.verdict != null && !feasibility.verdict.feasible;

  const blockOnFeasibility = (targetStep: WizardStep): boolean => {
    // CTV-536: dev-only escape hatch — the bypass toggle in InventoryDiagnostic
    // lets testers proceed while the booked-aggregate math is being fixed.
    if (bypassFeasibility) return false;
    // See feasibilityGate.ts: gate the advance into step 3+ on a resolved
    // *infeasible* verdict only — never the transient `loading` state (that was
    // the intermittent "Continue bounces me back to step 2" bug). Loading is
    // surfaced as a disabled "Confirming inventory…" Continue on the campaign
    // step, so the buyer waits there; the submit-time re-check is the backstop.
    // NOTE: the verdict is base-budget only today — upfront/promo/referral bonus
    // plays aren't fed into feasibility yet (known gap; see project memory).
    if (
      !shouldBlockAdvanceOnFeasibility({
        targetStep,
        loading: feasibility.loading,
        infeasible: isFeasibilityInfeasible,
      })
    ) {
      return false;
    }
    // Infeasible: surface the inventory warning card on step 2 (where the
    // budget/targeting inputs that fix it live). No-op when already on step 2;
    // a redirect for a step-rail jump that skipped step 2.
    if (currentStep !== 2) goToStep(2);
    const card = document.querySelector('[data-inventory-warning-card]');
    if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  };

  // Keep visitedSteps in sync if the user lands on a deeper step via URL
  // (e.g. opens a "resume your campaign" link).
  React.useEffect(() => {
    setVisitedSteps((prev) => (prev.includes(currentStep) ? prev : [...prev, currentStep]));
  }, [currentStep]);

  // CTV-471: a skip-verify user never reaches step 4 through normal
  // navigation, so landing on /start/verify means a bookmark or an old
  // magic-link — bounce to the campaign step (replace, so Back doesn't trap
  // them; step 2 because budget/dates may not be filled yet). Lives here, off
  // the same latch that hides the step, NOT in SelfServicePage off a separate
  // page-mount latch: that copy went stale when auth flipped mid-session
  // (sign-out, expired session) and bounced signed-out buyers from the end of
  // step 3 back to step 2 in an endless loop.
  React.useEffect(() => {
    if (currentStep === 4 && skipVerify) {
      navigate(`/${config.slug}/start/${slugFromStep(2)}`, { replace: true });
    }
  }, [currentStep, skipVerify, navigate, config.slug]);

  const handleNext = async () => {
    const valid = await validateCurrentStep();
    if (!valid) return;
    if (currentStep === 4 && !isEmailVerified) return;

    // CTV-421: when the campaign-setup step's custom geo tabs hold data on
    // more than one tab, the campaign can only commit to one — show the
    // conflict modal and gate the advance. The modal's confirm handler
    // re-fires this same handleNext after setting geoMode, which then
    // passes (because the resolved tab's data is what remains effective).
    //
    // CTV-533: empty-custom is enforced by the zod resolver in
    // campaignSetupSchema.superRefine and surfaces as a rose chip under
    // the "Narrow down" option; validateCurrentStep above blocks the
    // advance for it before we ever reach this branch.
    if (currentStep === 2) {
      const v = methods.getValues();
      const outcome = step2GeoOutcome({
        targetingMode: v.targetingMode,
        geoMode: v.geoMode,
        geoTargetZips: v.geoTargetZips,
        selectedStates: v.selectedStates,
        selectedDMAs: v.selectedDMAs,
      });
      if (outcome.kind === 'conflict-modal') {
        setGeoConflictOpen(true);
        return;
      }
      if (outcome.correctedGeoMode) {
        // Buyer's selection lives on a different tab than the currently
        // active geoMode — silently realign so the API serializer reads
        // the correct list. Without this the picked tab's data would be
        // dropped on submit.
        methods.setValue('geoMode', outcome.correctedGeoMode, { shouldDirty: true });
      }
    }

    // CTV-562 follow-up: the previous "Continue runs ensure-self while
    // creatingNew" branch is gone. Add-new-business now owns its own Save
    // button (BusinessInfoStep.handleSaveNewBusiness), which validates,
    // fires ensure-self, persists the contact, and clears creatingNew.
    // Continue is gated until that completes (onEditingChange feeds into
    // continueDisabled for both Edit and Add-new-business modes), so by
    // the time we reach this branch the seat already exists. The
    // /memberships/ensure-self call site for the truly-anonymous flow
    // (brand-new buyer who never visits step 1's seat creation) still
    // lives in the email-verify completion handler further down.

    // CTV-474: block the Continue advance into step 3+ when inventory is
    // infeasible. We re-check here (not just in validateCurrentStep) so the
    // gate applies regardless of which step the buyer is currently on.
    if (currentStep < MAX_STEP) {
      const nextStep = nextWizardStep(currentStep, skipVerify);
      if (blockOnFeasibility(nextStep)) return;
    }

    markStepCompleted(currentStep);
    analytics.track(EVENTS.WIZARD_STEP_COMPLETED, {
      step: slugFromStep(currentStep),
      step_number: currentStep,
      station_slug: config.slug,
    });
    if (currentStep < MAX_STEP) {
      goToStep(nextWizardStep(currentStep, skipVerify));
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      goToStep(prevWizardStep(currentStep, skipVerify));
    } else if (onExit) {
      onExit();
    }
  };

  const handleStepClick = async (step: WizardStep) => {
    if (step === currentStep) return;
    // If going forward, validate current step first
    if (step > currentStep) {
      // Same gate as the Continue button — keeps the top bar from
      // bypassing creative rejections, coverage gaps, the over-cap rule,
      // email verification, and the feasibility verdict. Without this,
      // a buyer on step 3 with an unresolved rejection could jump straight
      // to step 5 via the rail.
      if (continueDisabled) return;
      const valid = await validateCurrentStep();
      if (!valid) return;
      // Can't jump past verification step without verifying email.
      if (step > 4 && !isEmailVerified) return;
      // CTV-474: same gate as handleNext — block jumps into step 3+ when
      // inventory is infeasible. Catches step 1 -> step 3+ bypass attempts.
      if (blockOnFeasibility(step)) return;
      markStepCompleted(currentStep);
    }
    goToStep(step);
  };

  // Phase 1: upsert profile, persist the submission row, and request a
  // Stripe checkout intent from the API. Upfront returns a PaymentIntent;
  // monthly returns a SetupIntent for future off-session installments.
  // Triggered automatically on
  // step 4 entry (validateOnly = false skips re-checking acceptTerms which
  // the user hasn't toggled yet).
  // CTV-587/588: deferred-intent creator. Called once from the Payment
  // Element's Pay handler (after the US-card pre-check), NOT eagerly on step
  // entry or on plan toggles. Returns the new intent's client secret + type
  // for the caller to confirm against, or null when it surfaced its own
  // blocker (inventory regression, upload/API error already shown inline).
  const createPaymentIntent = async (
    retriedAfterStaleKey = false,
  ): Promise<{ clientSecret: string; intentType: 'payment' | 'setup' } | null> => {
    // Synchronous lock — guards against a double Pay click racing two creates.
    if (creatingIntentLockRef.current) return null;
    creatingIntentLockRef.current = true;

    // CTV-518: checkout is no longer gated on the publisher's Stripe
    // onboarding — CTVBuyer is the merchant of record, so the charge lands
    // on the platform account regardless of the station's payout state.

    setPaymentError(null);

    const values = methods.getValues();

    // Submit-time inventory re-check. If a publisher landed a sale between
    // step 2 and pay, the live verdict could regress to infeasible — block
    // before authorizing Stripe. Geo-lock alone doesn't block: we silently
    // override targetingMode to 'default' on the submission body if engaged.
    let recheckVerdict: CampaignFeasibilityResponse | null = null;
    try {
      if (
        !bypassFeasibility &&
        config.products[0]?.id &&
        values.budget &&
        values.budget > 0 &&
        values.startDate &&
        values.endDate
      ) {
        const customZips =
          values.targetingMode === 'custom' && values.geoMode === 'radius'
            ? (values.geoTargetZips ?? [])
            : undefined;
        recheckVerdict = await checkCampaignFeasibility({
          productId: config.products[0].id,
          budget: values.budget,
          startDate: values.startDate,
          endDate: values.endDate,
          paymentPlan: values.paymentPlan ?? 'upfront',
          targetingMode: values.targetingMode ?? 'default',
          geoTargetZips: customZips && customZips.length > 0 ? customZips : undefined,
        });
        if (!recheckVerdict.feasible) {
          setRegressionVerdict(recheckVerdict);
          creatingIntentLockRef.current = false;
          return null;
        }
      }
    } catch {
      // Don't block payment on a server-side feasibility-check failure.
      // The live check at step 2 already validated; we treat the re-check
      // as best-effort.
    }
    // CTV-474: the geo-lock fallback that silently reverted to station-wide
    // is gone. If the re-check finds a regression we surface the warning
    // modal; the submission payload below uses the buyer's chosen
    // targeting mode unchanged.
    void recheckVerdict;

    // `updateProfile` persists per-user contact info to /profiles/me. Business
    // identity (name + website) lives on the advertiser seat and is updated
    // separately via PATCH /advertisers/:id from the BusinessInfo step's
    // Edit form, not here.
    await updateProfile({
      contactName: values.contactName || undefined,
      contactPhone: values.contactPhone || undefined,
    });

    try {
      // 1. Wait for any in-flight uploads, then build the unified creatives[]
      //    payload. We finish uploads BEFORE creating the Stripe intent so a
      //    failed upload doesn't orphan a charge.
      const finalSlots = await slots.awaitUploads();
      const creativesPayload: Array<{
        storagePath: string;
        filename: string;
        mimeType?: string;
        sizeBytes?: number;
        clickThroughUrl?: string;
        segmentShares: Array<{ startDate: string; endDate: string; share: number }>;
        startDate?: string;
        endDate?: string;
      }> = [];
      for (const slot of finalSlots) {
        if (!slot.storagePath || !slot.filename) continue;
        const slotCtu = slot.clickThroughUrl?.trim();
        creativesPayload.push({
          storagePath: slot.storagePath,
          filename: slot.filename,
          mimeType: slot.mimeType,
          sizeBytes: slot.sizeBytes,
          clickThroughUrl: slotCtu
            ? /^https?:\/\//i.test(slotCtu) ? slotCtu : `http://${slotCtu}`
            : undefined,
          segmentShares: slot.segmentShares.map((s) => ({ ...s })),
          startDate: slot.startDate,
          endDate: slot.endDate,
        });
      }

      // Refuse to submit when the user picked a file but the upload didn't
      // land — awaitUploads() has already waited for any in-flight uploads
      // to settle, so a slot with `filename` but no `storagePath` is a
      // silently-failed upload. Without this gate the wizard would happily
      // POST `creatives: []` and create a paid campaign with no commercial
      // attached, leaving the detail view stuck on "No file uploaded".
      const attemptedSlots = finalSlots.filter((s) => s.filename || s.storagePath);
      if (attemptedSlots.length > 0 && creativesPayload.length < attemptedSlots.length) {
        throw new Error(
          "One of your commercials didn't finish uploading. Please retry the upload before continuing.",
        );
      }
      if (creativesPayload.length === 0) {
        throw new Error('Please upload at least one commercial before submitting.');
      }

      let advertiserId = selectedAdvertiser?.advertiserId ?? wizardAdvertiserId;
      if (!advertiserId) {
        const resp = await apiClient.post<{ membership: AdvertiserMembership; alreadyMember: boolean }>(
          '/memberships/ensure-self',
          {
            slug: config.slug,
            businessName: values.businessName.trim(),
            businessWebsite: values.businessWebsite?.trim() || null,
          },
        );
        advertiserId = resp.membership.advertiserId;
        setWizardAdvertiserId(resp.membership.advertiserId);
        setSelectedAdvertiserId(resp.membership.advertiserId);
        // CTV-582: inject the new membership synchronously so the wizard
        // body's next commit sees `selectedAdvertiser` populated. Avoids the
        // stale-closure `refreshMemberships` race that would clobber
        // advertiserMemberships back to null.
        seedMembership(resp.membership);
      }
      const paymentIdempotencyKey = getOrCreateStoredIdempotencyKey(paymentIdempotencyKeyKey);

      // CTV-466 follow-up: a Fix & Resubmit handoff lands here with
      // `resubmitTargetSubmissionId` set to the prior creative_rejected row.
      // Route to the in-place /resubmit endpoint so the existing row is
      // rebuilt rather than spawning a new one. Stripe Elements + Review
      // step are unchanged because the return shape matches /submissions.
      const submissionEndpoint = resubmitTargetSubmissionId
        ? `/submissions/${resubmitTargetSubmissionId}/resubmit`
        : '/submissions';
      const data = await apiClient.post<{
        submissionId?: string;
        clientSecret: string;
        intentType: 'payment' | 'setup';
        amountCents: number;
        serviceFeeCents: number;
        }>(submissionEndpoint, {
        slug: config.slug,
        advertiserId,
        campaign: {
          campaignName: values.campaignName?.trim() || '',
          budget: values.budget,
          selectedProduct: values.selectedProduct,
          startDate: values.startDate,
          endDate: values.endDate,
          targetMarkets: values.targetMarkets,
          // Custom-geo selections — serialized by the backend's
          // serializeTargetGeographies() into `target_geographies` based
          // on geoMode (the active tab). Format mirrors the non-selfservice
          // dashboard so downstream Publica push + receipt display can
          // read both worlds the same way.
          targetingMode: values.targetingMode ?? 'default',
          geoMode: values.geoMode,
          selectedStates: values.selectedStates ?? [],
          selectedDMAs: values.selectedDMAs ?? [],
          geoTargetCenter: values.geoTargetCenter ?? null,
          geoTargetRadiusMiles: values.geoTargetRadiusMiles ?? null,
          geoTargetZips: values.geoTargetZips ?? [],
        },
        creatives: creativesPayload,
        // Payment plan controls Stripe checkout behavior and, for monthly,
        // the local installment schedule. Campaign delivery remains one flight.
        paymentPlan: values.paymentPlan,
        referralCode: values.referralCode?.trim().toUpperCase() || null,
        promoCode: values.promoCode?.trim().toUpperCase() || null,
        previousSubmissionId: submissionIdRef.current ?? null,
        paymentIdempotencyKey,
        draftToken: draft.getToken(),
      });
      setSubmissionId(data.submissionId ?? null);
      // CTV-466 follow-up: clear the resubmit target now that the row has
      // been rebuilt — subsequent intent re-creations (e.g. a backtrack and
      // budget change) should go through the standard /submissions path with
      // `previousSubmissionId` cleanup, not /resubmit (which requires
      // creative_rejected status and would now 400).
      if (resubmitTargetSubmissionId) setResubmitTargetSubmissionId(null);
      setPaymentAmountCents(data.amountCents);
      setPaymentFeeCents(data.serviceFeeCents);
      analytics.track(EVENTS.PAYMENT_STARTED, {
        station_slug: config.slug,
        amount_cents: data.amountCents,
        payment_plan: values.paymentPlan,
      });
      return { clientSecret: data.clientSecret, intentType: data.intentType };
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to start payment';
      // The idempotency key lives in localStorage scoped by station slug only,
      // so a leftover key from a prior test / advertiser can collide with a
      // fresh submission. Two distinct backend errors signal the same recovery:
      // either the key was reused with different payment inputs (same
      // advertiser/station but budget/dates/etc changed), or it was already
      // used for a DIFFERENT advertiser+station pair. Both are safe to recover
      // by minting a brand-new key and retrying once — the new key creates a
      // fresh submission row rather than colliding with the stored one.
      const staleKey =
        /Payment attempt key was reused with different payment inputs/i.test(message) ||
        /Payment attempt key was already used for another submission/i.test(message);
      if (!retriedAfterStaleKey && staleKey) {
        clearPaymentIdempotencyKey();
        creatingIntentLockRef.current = false;
        return await createPaymentIntent(true);
      }
      // Sanitized: HTTP status only, never the raw message (may carry payload).
      analytics.track(EVENTS.PAYMENT_FAILED, {
        station_slug: config.slug,
        status: (e as { status?: number })?.status,
      });
      setPaymentError(message);
      return null;
    } finally {
      creatingIntentLockRef.current = false;
    }
  };

  // Phase 2: Stripe Payment Element succeeded → ask the backend to
  // synchronously pull card brand+last4 off the Stripe intent (so the
  // receipt is complete on first paint), then navigate. The finalize
  // call typically returns in <1s; if it errors or takes too long the
  // navigation still happens with whatever data we have.
  //
  // TODO: This handler has no unit test — exercising it requires mocking
  // react-router navigate + apiClient + Stripe Elements together, which
  // is significant scaffolding for limited coverage. The underlying
  // backend `/submissions/:id/finalize` endpoint is unit-tested in
  // selfservice-api/src/modules/submissions/submissions.service.spec.ts
  // (SubmissionsService.finalize tests), and the webhook-side persistence
  // is covered in stripe-routes.service.spec.ts. Tracking outside Linear
  // until issue limit clears.
  const handlePaymentSuccess = async () => {
    markStepCompleted(MAX_STEP);
    draft.clear();
    clearCapturedCode('ref', config.slug);
    clearCapturedCode('promo', config.slug);
    // Ref, not state: the id may have been created moments ago in this same
    // Pay handler (deferred flow), before React re-rendered this closure.
    const submittedId = submissionIdRef.current;
    if (!submittedId) return;
    setFinalizing(true);

    let finalizedRow: SelfServiceSubmissionRow | null = null;
    try {
      finalizedRow = await apiClient.post<SelfServiceSubmissionRow>(
        `/submissions/${submittedId}/finalize`,
        {},
      );
    } catch (e) {
      // CTV-518: finalize throws a 400 only for a policy decline — e.g. a
      // non-US card (US-only policy). The auth hold was released server-side, so
      // we must NOT show the success page. Surface the message and let the buyer
      // re-authorize with a US card on a fresh PaymentIntent. (Transient/5xx
      // errors fall through to the best-effort navigation below — the webhook
      // will populate the receipt fields.)
      if (e instanceof ApiError && e.status === 400) {
        // Policy decline (e.g. a non-US card). The auth hold was released
        // server-side. The deferred Payment Element is still mounted, so the
        // buyer just re-enters a US card and clicks Pay again. Clearing the
        // idempotency key means that next Pay mints a fresh key, and the
        // previousSubmissionId cleanup cancels this declined intent + row.
        setFinalizing(false);
        clearPaymentIdempotencyKey();
        setPaymentError(e.message);
        return;
      }
      // Webhook will populate the same fields eventually; SubmittedPage
      // shows a "receipt not ready" fallback in the meantime.
    }

    // The submission has now authorized — drop the localStorage marker so
    // a future wizard visit doesn't try to delete it as a "previous draft".
    // Also clear the payment-flow idempotency key (#686) so the next
    // submission attempt gets a fresh key rather than colliding.
    setSubmissionId(null);
    clearPaymentIdempotencyKey();

    const v = methods.getValues();
    // The conversion. Mirrored to GA4 (value/currency drive GA4 conversion
    // value); is_repeat marks a "run this campaign again" resubmission.
    analytics.track(EVENTS.SUBMISSION_COMPLETED, {
      submission_id: submittedId,
      station_slug: config.slug,
      budget: v.budget,
      product: v.selectedProduct,
      payment_plan: v.paymentPlan,
      has_referral: !!v.referralCode,
      has_promo: !!v.promoCode,
      is_repeat: !!rerunFrom,
      value: v.budget,
      currency: 'USD',
    });
    navigate(`/${config.slug}/submitted/${submittedId}`, {
      replace: true,
      state: {
        submission: finalizedRow ?? {
          id: submittedId,
          contact_email: v.contactEmail,
          business_info: {
            businessName: v.businessName,
            businessWebsite: v.businessWebsite,
            contactName: v.contactName,
            contactPhone: v.contactPhone,
          },
          campaign: {
            campaignName: v.campaignName,
            budget: v.budget,
            selectedProduct: v.selectedProduct,
            startDate: v.startDate,
            endDate: v.endDate,
            targetMarkets: v.targetMarkets,
          },
          payment_plan: v.paymentPlan,
          amount_cents: paymentAmountCents ?? 0,
          budget_cents: v.budget != null ? Math.round(v.budget * 100) : null,
          service_fee_cents: paymentFeeCents ?? 0,
          tax_cents: 0,
          created_at: toLocalIsoString(),
        },
      },
    });
  };

  const acceptTerms = useWatch({ control: methods.control, name: 'acceptTerms' });
  const acceptRefundPolicy = useWatch({ control: methods.control, name: 'acceptRefundPolicy' });
  const watchedEmail = useWatch({ control: methods.control, name: 'contactEmail' });
  // CTV-466 follow-up: when the user types (or reverts) the contact email
  // back to the email of the currently-authenticated user, pull their
  // profile name + phone onto the submission. We clear first so a partial
  // profile (only one of the two set) doesn't leave stale values from a
  // prior teammate the user had typed in. Idempotency ref ensures the
  // effect only fires on actual email-change events, not on every render.
  const lastContactEmailMatchRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!user?.email) return;
    const typed = (watchedEmail ?? '').trim().toLowerCase();
    const owner = user.email.trim().toLowerCase();
    if (!typed || typed !== owner) {
      lastContactEmailMatchRef.current = null;
      return;
    }
    if (lastContactEmailMatchRef.current === typed) return;
    lastContactEmailMatchRef.current = typed;
    methods.setValue('contactName', '', { shouldDirty: true });
    methods.setValue('contactPhone', '', { shouldDirty: true });
    if (user.contactName) {
      methods.setValue('contactName', user.contactName, { shouldDirty: true });
    }
    if (user.contactPhone) {
      methods.setValue('contactPhone', user.contactPhone, { shouldDirty: true });
    }
  }, [watchedEmail, user, methods]);
  const watchedPaymentPlan = useWatch({ control: methods.control, name: 'paymentPlan' });
  const watchedStartDate = useWatch({ control: methods.control, name: 'startDate' });
  const watchedEndDate = useWatch({ control: methods.control, name: 'endDate' });
  const watchedBudget = useWatch({ control: methods.control, name: 'budget' });
  const watchedReferralCode = useWatch({ control: methods.control, name: 'referralCode' });
  const watchedPromoCode = useWatch({ control: methods.control, name: 'promoCode' });

  // Stripe intent creation needs a sensible budget + dates. If the user
  // backtracks and clears one of these, gate the auto-create rather than
  // sending a bogus amountCents to Stripe (and getting a 400 back).
  const paymentInputsReady =
    typeof watchedBudget === 'number' && watchedBudget > 0
    && !!watchedStartDate && !!watchedEndDate;

  const codeValidation = useCodeValidation(
    config.slug,
    watchedReferralCode || '',
    watchedPromoCode || '',
    watchedEmail || undefined,
  );

  // Code- and prepay-side boosts no longer affect the Stripe charge — the
  // buyer pays full price and gets more ad plays for it (effective CPM
  // drops). The boost math + display lives in ReviewPayStep /
  // ImpressionsCalculator; the wizard just orchestrates the Stripe intent.

  // CTV-587/588: the Payment Element runs in deferred-intent mode, so there is
  // no eagerly-created Stripe intent on step entry and none to invalidate when
  // the buyer toggles the plan or edits budget/dates/codes. `createPaymentIntent`
  // is invoked once, on Pay. The Element's display `mode` + `amount` come
  // straight from the current form values below.
  const paymentMode: 'payment' | 'setup' =
    watchedPaymentPlan === 'monthly' ? 'setup' : 'payment';
  // Upfront authorizes the full grossed-up total; monthly (setup mode) charges
  // nothing today so the amount is display-only and unused by the Element.
  const displayAmountCents =
    typeof watchedBudget === 'number' && watchedBudget > 0
      ? estimateTotalWithServiceFeeCents(
          Math.round(watchedBudget * 100),
          config.serviceFeeRates,
        )
      : 0;

  // CTV-587/588: fetch the CustomerSession secret once the buyer reaches the
  // pay step while signed in, so the Payment Element can offer "save this card"
  // and redisplay their saved cards. Independent of budget/plan, so it's
  // fetched a single time (guarded by the in-flight ref + the null check) and
  // survives plan toggles. Best-effort: a failure just falls back to plain
  // card entry — it must not block payment.
  React.useEffect(() => {
    if (currentStep !== 5) return;
    if (!isAuthenticated) return;
    if (customerSessionSecret || customerSessionInFlightRef.current) return;
    customerSessionInFlightRef.current = true;
    void (async () => {
      try {
        const resp = await apiClient.post<{ customerSessionClientSecret: string }>(
          '/submissions/customer-session',
          { businessName: methods.getValues().businessName?.trim() || null },
        );
        setCustomerSessionSecret(resp.customerSessionClientSecret);
      } catch (e) {
        console.warn('[wizard] customer-session fetch failed; plain card entry:', e);
      } finally {
        customerSessionInFlightRef.current = false;
      }
    })();
  }, [currentStep, isAuthenticated, customerSessionSecret, methods]);

  // If the user is already signed in with a matching email, treat it as verified.
  React.useEffect(() => {
    if (isAuthenticated && user?.email && watchedEmail && user.email.toLowerCase() === watchedEmail.toLowerCase()) {
      setVerifiedEmail(user.email.toLowerCase());
    }
  }, [isAuthenticated, user?.email, watchedEmail]);

  // If the email changes away from the verified one, invalidate verification.
  React.useEffect(() => {
    if (verifiedEmail && watchedEmail && verifiedEmail !== watchedEmail.toLowerCase()) {
      setVerifiedEmail(null);
    }
  }, [watchedEmail, verifiedEmail]);

  const isEmailVerified = !!verifiedEmail && !!watchedEmail && verifiedEmail === watchedEmail.toLowerCase();

  // Single source of truth for "the buyer can't proceed from the step they're
  // standing on right now." Used by the Continue button (disabled state), the
  // top-bar `handleStepClick` forward guard, and the progress bar
  // (`forwardNavDisabled` prop greys out future pills so the rail can't
  // bypass these gates). The RHF schema gates aren't included — those
  // surface as field errors on Continue and are caught by
  // `validateCurrentStep()` inside handleStepClick. This boolean is only for
  // the non-schema gates (creative rejections, coverage, over-cap,
  // feasibility, email verification) where the field errors don't fire.
  // `handleStepClick` reads it via closure at click time, after this render
  // pass, so the forward-of-handleStepClick declaration order is fine.
  const continueDisabled =
    (currentStep === 1 && step1Editing) ||
    (currentStep === 4 && !isEmailVerified) ||
    (currentStep === 3 && hasUnresolvedRejections) ||
    (currentStep === 3 && hasCreativeCoverageGap) ||
    (currentStep === 3 && hasCreativeOverCap) ||
    (currentStep === 2 && feasibility.loading && !bypassFeasibility) ||
    (currentStep === 2 && !!feasibility.verdict && !feasibility.verdict.feasible && !bypassFeasibility);

  return (
    <div
      className="max-w-6xl mx-auto max-sm:pb-24"
      style={accentVars(config.accentColor || config.primaryColor, config.primaryColor)}
    >
      <WaymarkProvider
        // Key on the effective advertiser identity so a multi-seat switch
        // forces a fresh provider mount (clears inflightUrlRef + status) and
        // the auto-kickoff's startGenerationForUrl can't no-op against the
        // previous run's URL. `resolvedAdvertiserId` covers both the
        // selected-seat path and the brand-new-seat path (wizardAdvertiserId
        // populated at step 4 via ensure-self).
        key={resolvedAdvertiserId ?? 'anon'}
        slug={config.slug}
        draftToken={draft.token}
        initial={draft.loaded?.waymark ?? (draft.isLoading ? null : {
          status: 'idle',
          sourceUrl: null,
          topicId: null,
          generationRequestId: null,
          videoId: null,
          renderedUrl: null,
          errorCode: null,
          errorMessage: null,
        })}
      >
      <FormProvider {...methods}>
        <WaymarkAutoKickoff currentStep={currentStep} />
        <FeasibilityBridge config={config} onChange={setFeasibility} />
        <RadiusRecommendationBridge config={config} onChange={setRadiusRecommendation} />
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-8">
          <div className="min-w-0">
            <SelfServiceProgressBar
              currentStep={currentStep}
              completedSteps={completedSteps}
              visitedSteps={visitedSteps}
              onStepClick={handleStepClick}
              accentColor={config.accentColor || config.primaryColor}
              skipVerify={skipVerify}
              forwardNavDisabled={continueDisabled}
            />
            <div className="p-4 sm:p-6 md:p-8">
          {currentStep === 1 && (
            <BusinessInfoStep
              slug={config.slug}
              accentColor={config.accentColor || config.primaryColor}
              primaryColor={config.primaryColor}
              onEditingChange={setStep1Editing}
            />
          )}
          {currentStep === 2 && (
            <CampaignSetupStep
              config={config}
              feasibility={feasibility}
              radiusRecommendation={radiusRecommendation}
              bypassFeasibility={bypassFeasibility}
              onToggleBypassFeasibility={setBypassFeasibility}
            />
          )}
          {currentStep === 3 && hasUnresolvedRejections && (() => {
            // CTV-466 follow-up: when the wizard was opened from Fix &
            // Resubmit, surface a top-of-step summary of every rejected
            // slot so the advertiser doesn't have to scroll the slot cards
            // to learn what's broken. Unmounts the moment every rejected
            // slot has been replaced or removed (the same gate that drives
            // the Continue button).
            type RejSlot = { label: string; codes: string[] | null };
            const rejected: RejSlot[] = [];
            slots.slots.forEach((s, i) => {
              if (s.rejected) {
                rejected.push({
                  label: s.filename || `Commercial ${i + 1}`,
                  codes: s.rejectionReasonCodes ?? null,
                });
              }
            });
            const count = rejected.length;
            return (
              <div className="mb-5">
                <Banner
                  tone="warn"
                  label={count === 1
                    ? '1 commercial needs to be fixed'
                    : `${count} commercials need to be fixed`}
                >
                  <ul className="space-y-1 mb-2">
                    {rejected.map((s, i) => (
                      <li key={`${s.label}-${i}`}>
                        <span className="font-medium text-rose-900">{s.label}</span>
                        {s.codes && s.codes.length > 0 && (
                          <>
                            {' — '}
                            <span className="text-rose-700/90">
                              {s.codes
                                .map((c) => REJECTION_REASONS_BY_CODE[c]?.label)
                                .filter(Boolean)
                                .join(', ')}
                            </span>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                  Replace or remove each rejected commercial below, then continue.
                </Banner>
              </div>
            );
          })()}
          {currentStep === 3 && (
            <UploadCreativeStep
              slug={config.slug}
              accentColor={config.accentColor || config.primaryColor}
              primaryColor={config.primaryColor}
              secondaryColor={config.secondaryColor}
              campaignStartDate={watchedStartDate0}
              campaignEndDate={watchedEndDate0}
              slots={slots.slots}
              files={slots.files}
              uploadState={slots.uploadState}
              onFileChange={handleSlotFileChange}
              onSlotChange={slots.updateSlot}
              onSlotRemove={handleSlotRemove}
              onAddSlot={handleAddSlot}
              onResetWeights={handleResetWeights}
              advertiserId={selectedAdvertiser?.advertiserId ?? null}
              onPickFromLibrary={handleSlotPickFromLibrary}
              onCoverageChange={setCreativeCoverageGaps}
              onConcurrencyChange={setCreativeOverCapRanges}
              waymarkSlotKey={waymarkSlotKey}
              onCommitWaymark={handleCommitWaymark}
              onReplaceWaymark={handleReplaceWaymark}
              waymarkRejection={waymarkRejection}
              onClearWaymarkRejection={clearWaymarkRejection}
            />
          )}
          {(currentStep === 4 || currentStep === 5) && (() => {
            // Surface any in-flight upload or upload error to the user as
            // they leave step 3. Once the slot has a storagePath, the bytes
            // are on Supabase and we hide the indicator.
            const inFlight = Object.entries(slots.uploadState).filter(
              ([slotId, state]) => state.uploading && !slots.slots.find((s) => s.slotId === slotId)?.storagePath,
            );
            const errored = Object.entries(slots.uploadState).filter(([, state]) => !!state.error);
            if (errored.length === 0 && inFlight.length === 0) return null;
            return (
              <div className="mb-4">
                {errored.length > 0 ? (
                  <Banner
                    tone="warn"
                    label="Video upload failed"
                    rightSlot={
                      <button
                        type="button"
                        onClick={() => goToStep(3)}
                        className="text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full text-rose-700 hover:bg-rose-100/60"
                      >
                        Go back to step 3
                      </button>
                    }
                  >
                    {errored[0][1].error}
                  </Banner>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[13px] text-slate-600 inline-flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-full border-2 border-slate-300 border-t-slate-500 animate-spin" />
                    Uploading video…
                  </div>
                )}
              </div>
            );
          })()}
          {currentStep === 4 && (
            <VerifyEmailStep
              email={watchedEmail || ''}
              stationSlug={config.slug}
              onEmailChange={(next) => {
                methods.setValue('contactEmail', next, { shouldValidate: true, shouldDirty: true });
              }}
              isVerified={isEmailVerified}
              onVerified={async () => {
                setVerifiedEmail((watchedEmail || '').toLowerCase());
                // Seat redesign: a brand-new buyer who just verified an email
                // doesn't have any advertiser seat yet. Create one from the
                // BusinessInfo form fields so step 5 has somewhere to attach
                // the submission. Existing members (returning user) skip this
                // — their selected advertiser is already populated by
                // /memberships/mine on session load.
                if (!selectedAdvertiser && !ensureSelfInFlightRef.current) {
                  ensureSelfInFlightRef.current = true;
                  setEnsureSelfError(null);
                  try {
                    const values = methods.getValues();
                    const resp = await apiClient.post<{ membership: AdvertiserMembership; alreadyMember: boolean }>(
                      '/memberships/ensure-self',
                      {
                        slug: config.slug,
                        businessName: values.businessName,
                        businessWebsite: values.businessWebsite || null,
                      },
                    );
                    setWizardAdvertiserId(resp.membership.advertiserId);
                    setSelectedAdvertiserId(resp.membership.advertiserId);
                    // CTV-582: seed the membership synchronously into useAuth
                    // so the wizard body re-renders with `selectedAdvertiser`
                    // populated on the very next commit. Prior implementation
                    // awaited refreshMemberships(), whose pre-verify-captured
                    // closure gated on the stale React `user?.id` and clobbered
                    // advertiserMemberships back to null (see comment header
                    // for the full post-verify hydration handoff).
                    seedMembership(resp.membership);
                  } catch (e) {
                    setEnsureSelfError(
                      e instanceof Error ? e.message : 'Failed to create your advertiser seat.',
                    );
                    ensureSelfInFlightRef.current = false;
                    return; // don't mark step complete
                  }
                  ensureSelfInFlightRef.current = false;
                }
                markStepCompleted(4);
              }}
              accentColor={config.accentColor || config.primaryColor}
              primaryColor={config.primaryColor}
            />
          )}
          {currentStep === 4 && ensureSelfError && (
            <div className="mt-4">
              <Banner tone="warn" label="Couldn't continue">{ensureSelfError}</Banner>
            </div>
          )}
          {currentStep === 5 && (
            <ReviewPayStep
              config={config}
              codeValidation={codeValidation}
              slots={slots.slots}
              slug={config.slug}
              skipVerify={skipVerify}
            />
          )}

          {currentStep === 5 && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Payment</h3>
              {!paymentInputsReady && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                  Go back to the Campaign step and fill in budget + dates to continue.
                </div>
              )}
              {finalizing && (
                <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-5 text-slate-700">
                  <Loader2 className="w-5 h-5 animate-spin shrink-0" />
                  <div className="text-sm">
                    <div className="font-semibold">Finalizing your order…</div>
                    <div className="text-slate-500 mt-0.5">
                      {paymentMode === 'setup'
                        ? 'Saving your payment method and preparing your receipt. This usually takes a second.'
                        : 'Confirming the payment hold and preparing your receipt. This usually takes a second.'}
                    </div>
                  </div>
                </div>
              )}
              {/* CTV-587/588: deferred-intent Payment Element. It mounts as soon
                  as budget/dates + a chosen plan exist (no server round-trip
                  first), and toggling the plan updates its mode/amount in place
                  without wiping the card. The Stripe intent is created on Pay
                  via `createPaymentIntent`. */}
              {!finalizing && paymentInputsReady && watchedPaymentPlan && (
                <StripePaymentSection
                  mode={paymentMode}
                  customerSessionClientSecret={customerSessionSecret ?? undefined}
                  accentColor={config.accentColor || config.primaryColor}
                  amountCents={displayAmountCents}
                  disabled={!acceptTerms || !acceptRefundPolicy}
                  disabledReason={
                    !acceptTerms && !acceptRefundPolicy
                      ? 'Accept the terms and refund policy above to enable payment'
                      : !acceptTerms
                        ? 'Accept the terms above to enable payment'
                        : !acceptRefundPolicy
                          ? 'Acknowledge the refund policy above to enable payment'
                          : undefined
                  }
                  createIntent={createPaymentIntent}
                  onSuccess={handlePaymentSuccess}
                  onError={(msg) => setPaymentError(msg)}
                  beforeButton={
                    <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <div className="font-semibold text-emerald-900">
                          {paymentMode === 'setup'
                            ? 'No charge today. Your card is saved for monthly billing.'
                            : 'Your card is only charged after your ad is approved.'}
                        </div>
                        <p className="text-emerald-800 mt-0.5">
                          {paymentMode === 'setup'
                            ? 'If your commercial is approved, the first installment is charged on your campaign start date and future installments charge on the schedule shown above. Charges appear as CTVBuyer.'
                            : 'We place a temporary authorization hold until approval. Charges appear as CTVBuyer.'}
                        </p>
                      </div>
                    </div>
                  }
                />
              )}
            </div>
          )}

          {paymentError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {paymentError}
            </div>
          )}

          {/* Navigation — sticky to viewport bottom on mobile, inline on desktop */}
          <div className="
            mt-8 pt-6
            max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:z-30
            max-sm:bg-white/85 max-sm:backdrop-blur-xl max-sm:border-t max-sm:border-slate-200/70
            max-sm:px-4 max-sm:py-3 max-sm:mt-0 max-sm:pt-3
            max-sm:shadow-[0_-4px_16px_-8px_rgba(15,23,42,0.1)]
          ">
            {currentStep === 2 && (
              // CTV-474: mirror the form-content container's horizontal
              // padding (p-4 sm:p-6 md:p-8) so the warning card lines up
              // with the targeting cards above. The footer otherwise has no
              // horizontal padding on desktop.
              <div className="mb-4 px-4 sm:px-6 md:px-8 max-sm:px-0">
                <InventoryWarningCard
                  verdict={feasibility.verdict}
                  accentColor={config.accentColor || config.primaryColor}
                  isRadiusMode={methods.watch('geoMode') === 'radius'}
                  radiusMiles={methods.watch('geoTargetRadiusMiles')}
                  currentRadiusCenter={methods.watch('geoTargetCenter') ?? null}
                  onApplyExtensionDate={(date) =>
                    methods.setValue('endDate', date, { shouldValidate: true, shouldDirty: true })
                  }
                  onRemoveGeoTargeting={() => {
                    // CTV-474: flip back to run-on-station. We leave the
                    // previous geo selections in the form so the buyer can
                    // re-enable custom targeting without re-entering them.
                    methods.setValue('targetingMode', 'default', {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  onApplyRadiusCenter={(lat, lon, label) => {
                    // CTV-474: clicking "Center on X" only moves the pin —
                    // the buyer still owns the radius slider. Form state
                    // uses `lng` as the property name; the warning card
                    // emits `lon` to match the Postgres convention.
                    methods.setValue(
                      'geoTargetCenter',
                      { lat, lng: lon, label },
                      { shouldValidate: true, shouldDirty: true },
                    );
                  }}
                  onAddGeoCodes={(kind, codes) => {
                    if (kind === 'states') {
                      const existing = methods.getValues('selectedStates') ?? [];
                      methods.setValue(
                        'selectedStates',
                        Array.from(new Set([...existing, ...codes])),
                        { shouldValidate: true, shouldDirty: true },
                      );
                    } else if (kind === 'dmas') {
                      const existing = methods.getValues('selectedDMAs') ?? [];
                      methods.setValue(
                        'selectedDMAs',
                        Array.from(new Set([...existing, ...codes])),
                        { shouldValidate: true, shouldDirty: true },
                      );
                    }
                    // CTV-502: kind === 'zips' is no longer reachable in
                    // the live wizard — geoTargetZips only flows through
                    // radius mode, which the warning card re-labels to
                    // 'radius'. No-op kept implicit.
                  }}
                />
              </div>
            )}
            <div className="flex flex-row items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleBack}
              disabled={currentStep === 1 && !onExit}
              className={`group inline-flex items-center justify-center gap-1.5 w-full sm:w-auto px-3 py-2 rounded-full text-[13px] font-medium tracking-tight transition-colors ${
                currentStep === 1 && !onExit
                  ? 'text-slate-300 cursor-not-allowed'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
              Back
            </button>

            {currentStep < MAX_STEP ? (
              <div className="flex flex-col items-end gap-1.5">
                {currentStep === 1 && step1Editing && (
                  <p className="text-[11px] text-slate-500 max-w-xs text-right">
                    Save or discard your changes to continue.
                  </p>
                )}
                {currentStep === 2 && feasibility.loading && !bypassFeasibility && (
                  <p className="text-[11px] text-slate-500 max-w-xs flex items-center justify-end gap-1.5 ml-auto">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Confirming inventory before you continue…
                  </p>
                )}
                {currentStep === 3 && hasUnresolvedRejections && (
                  <p className="text-[11px] text-rose-700 max-w-xs text-right">
                    Replace or remove the rejected commercials to continue.
                  </p>
                )}
                {currentStep === 3 && !hasUnresolvedRejections && hasCreativeCoverageGap && (
                  <p className="text-[11px] text-amber-800 max-w-xs text-right">
                    Add a commercial for every day of your campaign to continue.
                  </p>
                )}
                {currentStep === 3 &&
                  !hasUnresolvedRejections &&
                  !hasCreativeCoverageGap &&
                  hasCreativeOverCap && (
                  <p className="text-[11px] text-amber-800 max-w-xs text-right">
                    Reduce overlapping commercials to continue.
                  </p>
                )}
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={continueDisabled}
                  className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-2.5 rounded-full text-[13px] font-semibold tracking-tight text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100"
                  style={{
                    background: `linear-gradient(180deg, ${config.accentColor || config.primaryColor}, ${config.primaryColor})`,
                    boxShadow: `0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px -8px ${config.primaryColor}`,
                  }}
                >
                  Continue
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>
            ) : null}
            </div>
          </div>
        </div>
          </div>
          <div className="hidden lg:flex lg:flex-col lg:gap-4 self-start sticky top-32">
            <CampaignSummarySidebar
              config={config}
              creativeFileName={slots.slots[0]?.filename || undefined}
              commercialCount={slots.slots.filter((s) => s.storagePath || s.filename).length}
              codeValidation={codeValidation}
            />
            {/* CTV-474: inventory warning card now mounts in the wizard footer
                directly above the Continue button (step 2 only). */}
          </div>
        </div>
        {regressionVerdict && (
          <InventoryRegressionModal
            verdict={regressionVerdict}
            accentColor={config.accentColor || config.primaryColor}
            onEditCampaign={() => {
              setRegressionVerdict(null);
              goToStep(2);
            }}
            onDismiss={() => setRegressionVerdict(null)}
          />
        )}
        {geoConflictOpen && (
          <GeoConflictModal
            selectedStates={methods.getValues('selectedStates') ?? []}
            selectedDMAs={methods.getValues('selectedDMAs') ?? []}
            radiusZips={methods.getValues('geoTargetZips') ?? []}
            currentMode={methods.getValues('geoMode') ?? 'radius'}
            accentColor={config.accentColor || config.primaryColor}
            onCancel={() => setGeoConflictOpen(false)}
            onConfirm={(chosen) => {
              methods.setValue('geoMode', chosen, { shouldDirty: true });
              // Drop the other tabs' selections — the user just committed
              // to one. Keeps subsequent advances from re-prompting.
              if (chosen !== 'radius') {
                methods.setValue('geoTargetCenter', null, { shouldDirty: true });
                methods.setValue('geoTargetRadiusMiles', undefined, { shouldDirty: true });
                methods.setValue('geoTargetZips', [], { shouldDirty: true });
              }
              if (chosen !== 'states') methods.setValue('selectedStates', [], { shouldDirty: true });
              if (chosen !== 'dmas') methods.setValue('selectedDMAs', [], { shouldDirty: true });
              setGeoConflictOpen(false);
              // Re-advance now that the conflict is resolved.
              void handleNext();
            }}
          />
        )}
      </FormProvider>
      </WaymarkProvider>
    </div>
  );
};

// Loading shell shown while the initial draft load is in flight. Renders
// the wizard's outer container + a static progress bar so the layout doesn't
// pop into existence; the form body only mounts once the draft has loaded.
const SelfServiceWizardLoading: React.FC<{ config: PublisherConfig; currentStep: WizardStep }> = ({ config, currentStep }) => {
  const accentColor = config.accentColor || config.primaryColor;
  return (
    <div
      className="max-w-6xl mx-auto max-sm:pb-24"
      style={accentVars(accentColor, config.primaryColor)}
    >
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-8">
        <div className="min-w-0">
          <SelfServiceProgressBar
            currentStep={currentStep}
            completedSteps={[]}
            visitedSteps={[currentStep]}
            onStepClick={() => { /* disabled while loading */ }}
            accentColor={accentColor}
            skipVerify={false}
          />
          <div className="p-12 flex flex-col items-center justify-center gap-3 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: accentColor }} />
            <p className="text-[13px]">Loading your campaign…</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Public entry point. Holds `useSelfServiceDraft` so the form body only
// mounts once the initial load has settled — this is what prevents the
// post-mount `methods.reset(seed)` cascade that used to clip focus on
// step 2's campaign-name and budget inputs.
//
// CTV-554: the body is keyed by `${advertiserId}:${userId}` so identity
// flips (seat switch, sign-in, sign-out, sign-in-as-different-user)
// remount the body fresh. `useForm` re-initializes from the new slot's
// draft data — no post-mount reset cascade, no stale data leaking
// across identities. The previous `firstLoadDoneRef` latch was specific
// to "first load only"; we now gate every load on `isLoading` (which
// flips to true at the top of the load effect on any identity change),
// so the latch is no longer needed.
const SelfServiceWizard: React.FC<SelfServiceWizardProps> = (props) => {
  const { user, selectedAdvertiser, isAuthenticated } = useAuth();
  // CTV-471: only a user who was ALREADY signed in when this wizard session
  // started skips the Verify step (4). A brand-new buyer who verifies their
  // email mid-session becomes authenticated, but must KEEP the Verify step
  // for the rest of the session — it shouldn't vanish the moment they
  // complete it, and they should still be able to navigate back to it.
  //
  // Latched HERE (this wrapper mounts once per wizard entry) and not in the
  // Body: the Body is keyed by identity below, so a Body-level latch would
  // re-latch on the very sign-in/sign-out it's meant to ignore. (AuthProvider
  // gates render on the session bootstrap, so isAuthenticated is settled on
  // this first render.)
  const skipVerify = React.useRef(isAuthenticated).current;
  const advertiserId = selectedAdvertiser?.advertiserId ?? null;
  const userId = user?.id ?? null;
  const draft = useSelfServiceDraft(props.config.slug, advertiserId, userId);

  if (draft.isLoading) {
    return <SelfServiceWizardLoading config={props.config} currentStep={props.currentStep} />;
  }
  const bodyKey = `${advertiserId ?? 'anon'}:${userId ?? 'anon'}`;
  return <SelfServiceWizardBody key={bodyKey} {...props} draft={draft} skipVerify={skipVerify} />;
};

export default SelfServiceWizard;
